import {
  Box,
  Flex,
  IconArrow,
  IconHome,
  Link,
  ListItem,
  Text,
  UnorderedList,
  useBreakpointValue,
} from '@gazin/ecommerce-ui'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from 'react-icons/fa'
import {
  CategoryReponse,
  ParentCategoryResponse,
} from 'src/services/marketplace-api/dtos'
import { CategorySkeleton } from './category-skeleton'
import { getStyles } from './styles'

type BreadcrumbsCategoryProps = Omit<
  CategoryReponse,
  'categoria_filhos' | 'situacao'
>

export type BreadcrumbsProps = {
  productTitle?: string
  productSlug?: string
  category?: BreadcrumbsCategoryProps
  loading?: boolean
  social?: boolean
}

export type ProductBreadcrumbBuilderProps = {
  slug: string
  nome: string
}

const Breadcrumbs = ({
  productTitle,
  productSlug,
  category,
  loading,
  social,
}: BreadcrumbsProps) => {
  const [breadcrumb, setBreadcrumb] = useState<
    ProductBreadcrumbBuilderProps[] | null
  >([])
  const router = useRouter()

  const isMobile = useBreakpointValue(
    { base: true, sm: true, md: false },
    { fallback: 'lg' }
  )

  const productBreadcrumbBuilder = (
    data: BreadcrumbsCategoryProps | ParentCategoryResponse
  ): ProductBreadcrumbBuilderProps[] => {
    const category = data as BreadcrumbsCategoryProps
    const categoria_pai = category.categoria_pai

    if (categoria_pai) {
      return [
        {
          slug: `/categoria/${categoria_pai.slug}`,
          nome: categoria_pai.nome,
        },
        ...productBreadcrumbBuilder(categoria_pai),
      ]
    }

    return []
  }

  const isProductReviewPage = router.pathname.includes(
    '/produto/[id]/avaliacoes'
  )

  const buildProductReviewBreadcrumbs = (
    category: BreadcrumbsCategoryProps
  ) => {
    const productId = router.query.id
    const breadcrumbItem = {
      slug: `/produto/${productSlug}/${productId}`,
      nome: productTitle || '',
    }

    if (category && !isMobile) {
      const categoriesBreadcrumbItems = buildBreadcrumbsItemByCategory(category)
      return [...categoriesBreadcrumbItems, breadcrumbItem]
    }

    return [breadcrumbItem]
  }

  const buildBreadcrumbsItemByCategory = (
    category: BreadcrumbsCategoryProps
  ) => {
    const breadCrumbItem = [
      {
        slug: `/categoria/${category.slug}`,
        nome: category.nome,
      },
      ...productBreadcrumbBuilder(category),
    ]

    return breadCrumbItem.reverse()
  }

  const buildBreadcrumbsItemByPathname = (pathname: string) => {
    if (pathname.startsWith('/busca/')) {
      const searchQuery = router?.query?.busca?.[0] ?? ''
      return [
        {
          slug: router.asPath,
          nome: `Resultados para ${searchQuery.toLowerCase()}`,
        },
      ]
    }

    const ROUTES_MAP = {
      '/conta': { slug: 'conta', nome: 'Meus Dados' },
      '/conta/dados': { slug: 'conta', nome: 'Meus Dados' },
      '/conta/alterar/email': {
        slug: '/conta/alterar/email',
        nome: 'Alterar E-mail',
      },
      '/conta/alterar/senha': {
        slug: '/conta/alterar/senha',
        nome: 'Alterar Senha',
      },
      '/conta/pedidos': { slug: '/conta/pedidos', nome: 'Meus Pedidos' },
      '/conta/viagens': { slug: '/conta/viagens', nome: 'Acompanhar viagens' },
      '/conta/minhas-avaliacoes': {
        slug: '/conta/minhas-avaliacoes',
        nome: 'Minhas avaliações',
      },
      '/conta/segunda-via': {
        slug: '/conta/segunda-via',
        nome: '2ª Via Do Boleto',
      },
      '/conta/enderecos': { slug: '/conta/enderecos', nome: 'Meus Endereços' },
      '/conta/tickets': {
        slug: '/conta/tickets',
        nome: 'Acompanhar Atendimentos',
      },
    }

    const breadcrumbItem = [{ slug: '/conta', nome: 'Minha Conta' }]
    const foundBreadcrumb = ROUTES_MAP[pathname as keyof typeof ROUTES_MAP]

    if (foundBreadcrumb) {
      breadcrumbItem.push(foundBreadcrumb)

      return breadcrumbItem
    }

    const foundTicketBreadcrumb = pathname.includes('/conta/tickets/')
    if (foundTicketBreadcrumb) {
      breadcrumbItem.push(
        {
          slug: '/conta/tickets',
          nome: 'Acompanhar Atendimentos',
        },
        {
          slug: `/conta/tickets/${router.query.tickets}`,
          nome: 'Detalhes do Atendimento',
        }
      )

      return breadcrumbItem
    }

    const foundReviewBreadcrumb = pathname.includes('/conta/minhas-avaliacoes/')
    if (foundReviewBreadcrumb) {
      breadcrumbItem.push(
        {
          slug: '/conta/minhas-avaliacoes',
          nome: 'Minhas Avaliações',
        },
        {
          slug: `/conta/minhas-avaliacoes/avaliar/${router.query.orderItemId}`,
          nome: 'Avaliar',
        }
      )

      return breadcrumbItem
    }

    return breadcrumbItem
  }

  const buildBreadcrumbs = () => {
    if (productSlug && productTitle && category) {
      return setBreadcrumb(buildProductReviewBreadcrumbs(category))
    }
    if (category) {
      return setBreadcrumb(buildBreadcrumbsItemByCategory(category))
    }
    setBreadcrumb(buildBreadcrumbsItemByPathname(router.pathname))
  }

  useEffect(() => {
    buildBreadcrumbs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, router.asPath, isMobile])

  const styles = getStyles()

  const socialMedia = [
    {
      link: 'https://www.facebook.com/gazin',
      icone: <FaFacebookF {...styles.socialMediaIcon} />,
    },
    {
      link: 'https://www.instagram.com/gazin/',
      icone: <FaInstagram {...styles.socialMediaIcon} />,
    },
    {
      link: 'https://www.youtube.com/user/gazinoficial',
      icone: <FaYoutube {...styles.socialMediaIcon} />,
    },
    {
      link: 'https://www.linkedin.com/company/2450400?trk=tyah&trkInfo=tarId%3A1421946155243%2Ctas%3AGazin%2Cidx%3A2-1-6',
      icone: <FaLinkedinIn {...styles.socialMediaIcon} />,
    },
  ]

  return (
    <Box
      borderTop="0.0625rem solid #e8e8e8"
      data-testid="breadcrumb-box"
      marginBottom="0rem"
      minHeight={['2.6875rem', '2.5rem']}
      paddingTop={['0.9375rem', '1.375rem', '0.625rem']}
    >
      {!!breadcrumb?.length && (
        <Flex
          alignItems="center"
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
          justifyContent="space-between"
          marginLeft="auto"
          marginRight="auto"
          maxWidth="81.875rem"
          width="100%"
        >
          <UnorderedList
            alignItems="flex-start"
            display="flex"
            marginBottom={['0.625rem', '0rem']}
          >
            <Flex
              _first={{ marginLeft: '0rem' }}
              marginLeft={['1.625rem', '3.5rem']}
              position="relative"
            >
              <Link
                color="#0d71f0"
                data-testid="link-icon-home"
                display="block"
                fontSize={['0.75rem', '0.875rem']}
                _hover={{ textDecor: 'none' }}
                href="/"
                lineHeight="1.0625rem"
                textTransform="capitalize"
                textDecor="none"
              >
                <IconHome data-testid="icon-home" height="1rem" width="1rem" />
              </Link>
              <IconArrow
                color="#0950A9"
                data-testid="icon-arrow"
                height="0.7rem"
                position="absolute"
                right={['-1.0625rem', '-2rem']}
                top="0.1875rem"
                width="0.34rem"
              />
            </Flex>
            {loading ? (
              <CategorySkeleton isMobile={!!isMobile} />
            ) : (
              <>
                {breadcrumb.map((link, key) => {
                  const isLastItem = key === breadcrumb.length - 1

                  const linkColor = isLastItem ? '#646981' : '#0d71f0'

                  return (
                    <Flex
                      _first={{ marginLeft: '0rem' }}
                      key={link.slug}
                      marginLeft={['1.625rem', '3.5rem']}
                      position="relative"
                    >
                      <Link
                        color={linkColor}
                        data-testid="link-breadcrumb-item"
                        display="block"
                        fontSize={['0.75rem', '0.875rem']}
                        fontWeight={isLastItem ? '600' : '400'}
                        _hover={{
                          textDecor: 'none',
                          color: linkColor,
                        }}
                        href={link.slug}
                        lineHeight="1.0625rem"
                        textDecor="none"
                      >
                        <Text
                          margin={0}
                          fontSize={['0.75rem', '0.875rem']}
                          noOfLines={isProductReviewPage ? 1 : undefined}
                        >
                          {link.nome}
                        </Text>
                      </Link>
                      {key < breadcrumb.length - 1 && (
                        <IconArrow
                          color="#0950A9"
                          height="0.7rem"
                          position="absolute"
                          right={['-1.0625rem', '-1.9875rem']}
                          top="0.1875rem"
                          width="0.34rem"
                        />
                      )}
                    </Flex>
                  )
                })}
              </>
            )}
          </UnorderedList>
          <Box
            alignItems="center"
            data-testid="social-midia-box"
            display={['none', 'flex']}
            visibility={social ? 'visible' : 'hidden'}
            opacity={social ? 1 : 0}
          >
            <Text
              color="#515568"
              fontSize="0.875rem"
              lineHeight="1.0625rem"
              marginBottom="0"
            >
              Acompanhe nas redes sociais
            </Text>
            <UnorderedList display="flex" marginRight="1rem">
              {socialMedia.map(({ icone, link }, key) => (
                <ListItem key={key} marginLeft="0.5rem">
                  <Link
                    alignItems="center"
                    backgroundColor="#e8edf4"
                    borderRadius="0.375rem"
                    display="flex"
                    flexDirection="column"
                    height="2rem"
                    href={link}
                    justifyContent="center"
                    rel="noopener noreferrer"
                    target="_blank"
                    width="2rem"
                  >
                    {icone}
                  </Link>
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
        </Flex>
      )}
    </Box>
  )
}

export default Breadcrumbs
